import { SelectedMultiOption } from '@weareopenr/component-library/build/components/MultiSelect/utilities';
import { INestedVenueCategory, IVenueCategory } from 'src/api/getVenueCategories';

type NestedCategory = { category_id: string | null; name: string; display_order: number; children: NestedCategory[] };

export const PARENT_GROUP_ID_SUFFIX = '__group';
export const UNCATEGORISED_ID = '__uncategorised';

export const stripGroupIds = (ids: string[]) => ids.filter((id) => !id.endsWith(PARENT_GROUP_ID_SUFFIX));

export const nestedCategoriesToSelectOptions = (
  nestedCategories: NestedCategory[],
  options?: { parentAsLeafLabel?: (categoryName: string) => string },
): SelectedMultiOption[] => {
  const mapCategory = (category: NestedCategory): SelectedMultiOption => {
    const children = category.children?.map(mapCategory) || [];
    const id = category.category_id ?? UNCATEGORISED_ID;
    const { parentAsLeafLabel } = options ?? {};
    const isGroupParent = !!parentAsLeafLabel && children.length > 0;

    return {
      id: isGroupParent ? `${id}${PARENT_GROUP_ID_SUFFIX}` : id,
      label: category.name,
      display_order: category.display_order,
      children: isGroupParent
        ? [{ id, label: parentAsLeafLabel(category.name), display_order: -1, children: [] }, ...children]
        : children,
    };
  };

  return nestedCategories?.map(mapCategory) || [];
};

export const venueCategoriesToSelectOptions = (nestedCategories: INestedVenueCategory[]): SelectedMultiOption[] =>
  nestedCategoriesToSelectOptions(nestedCategories);

export const venuesInSelectedCategories = (selected: string[] = [], categories: IVenueCategory[] = []): string[] => {
  if (selected.length === 0) return [];

  const venues = categories
    .filter(({ category_id }) => selected.includes(category_id ?? UNCATEGORISED_ID))
    .flatMap(({ venue_ids }) => venue_ids);
  return Array.from(new Set(venues));
};

export const filterVenueOptions = <T extends { id: string; label: string }>(
  options: T[],
  searchText: string | undefined,
  filteredCategoryIds: string[],
  categories: IVenueCategory[] = [],
): T[] => {
  const normalisedSearch = (searchText ?? '').toLowerCase();
  const venueIdsInCategories = new Set(venuesInSelectedCategories(filteredCategoryIds, categories));
  const isCategoryFilterActive = filteredCategoryIds.length > 0;

  return options.filter(
    ({ id, label }) =>
      label.toLowerCase().includes(normalisedSearch) && (!isCategoryFilterActive || venueIdsInCategories.has(id)),
  );
};

export const CATEGORIES_SEARCH_PARAM = 'categories';

export const parseCategoriesParam = (searchParams: URLSearchParams): string[] => {
  const raw = searchParams.get(CATEGORIES_SEARCH_PARAM);
  if (!raw) return [];
  return raw
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
};

export const serializeCategoriesParam = (selected: string[], search = ''): string => {
  const params = new URLSearchParams(search);
  if (selected.length === 0) {
    params.delete(CATEGORIES_SEARCH_PARAM);
  } else {
    params.set(CATEGORIES_SEARCH_PARAM, selected.join(','));
  }
  return params.toString();
};
